/* eslint-disable react/no-unescaped-entities --
 * v77.99.86: Dedicated Upcoming Expenses planning page. Hosts the
 * full UpcomingExpensesSection in "page" mode plus a Convert flow
 * that hands off to /app/expenses (the standard Add Expense dialog
 * has all of the receipt + split + reimbursement UI we need).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarClock } from "lucide-react";
import { UpcomingExpensesSection } from "@/components/expenses/UpcomingExpensesSection";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function UpcomingExpensesPage() {
  const { currency, isMediator } = useAuth();
  const navigate = useNavigate();
  const sectionRef = useRef(null);

  const [children, setChildren] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/children");
        setChildren(Array.isArray(data) ? data : []);
      } catch {
        // Non-fatal — section will still load without child names.
      }
    })();
  }, []);

  /**
   * Convert handler — the dedicated page can't host the full Add
   * Expense dialog (which lives on /app/expenses). Instead we deep-
   * link to the Expenses page with a query flag; the Expenses page
   * will pick it up and open its dialog prefilled. The completion
   * callback (stamp the heads-up) is invoked there.
   */
  function handleConvertReady(item, onLinked) {
    // Stash both the prefill and the linker on window so Expenses
    // page can hydrate the dialog and call back. Simpler than a
    // global context for a one-time hand-off.
    if (typeof window !== "undefined") {
      window.__upcomingConvertPrefill = item;
      window.__pendingHeadsUpLink = onLinked;
    }
    navigate("/app/expenses?convert_upcoming=1");
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="upcoming-expenses-page">
      <button
        onClick={() => navigate("/app/expenses")}
        className="inline-flex items-center gap-1.5 text-sm text-[#6F828A] hover:text-[#116B6E] mb-4 transition-colors"
        data-testid="upcoming-back-link"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Expenses
      </button>

      <header className="mb-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FBF0DC] flex items-center justify-center">
            <CalendarClock className="w-5 h-5 text-[#B88A3A]" aria-hidden />
          </div>
          <div className="min-w-0">
            <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#2E3A3F] leading-tight">
              Upcoming Expenses
            </h1>
            <p className="text-sm text-[#6F828A] mt-1 leading-relaxed max-w-xl">
              Track expected costs before they become real expenses.
              Flag big-ticket items, share with your co-parent, and convert
              them into tracked expenses once paid.
            </p>
          </div>
        </div>
      </header>

      <UpcomingExpensesSection
        ref={sectionRef}
        variant="page"
        currency={currency}
        childList={children}
        isMediator={isMediator}
        onConvertReady={handleConvertReady}
      />
    </div>
  );
}
